<template>
  <div class="mcp">
    <!-- 概览：访问受保护 -->
    <section class="dev-section">
      <h2 class="dev-section__title">Overview</h2>
      <div class="dev-card">
        <div class="dev-row">
          <div class="dev-row__text">
            <div class="dev-row__title">Ask about your machines</div>
            <div class="dev-row__sub">
              Connect Claude, ChatGPT or another AI assistant to your VTM account. It can look up machines, stock and sales for you.
            </div>
          </div>
        </div>
        <div class="dev-row">
          <div class="dev-row__text">
            <div class="dev-row__title">
              Access is protected
              <span class="vtm-pill vtm-pill--ok">Sign-in required</span>
              <span class="vtm-pill vtm-pill--info">Read-only</span>
            </div>
            <div class="dev-row__sub">
              Assistants sign in with your VTM email and password and only see machines on your account. They can read your data but can never change prices, stock or settings.
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 你的 MCP -->
    <section class="dev-section">
      <h2 class="dev-section__title">Your MCP</h2>
      <div class="dev-section__sub">Everything you need to connect an assistant</div>
      <div class="dev-card">
        <div class="dev-row dev-row--stack">
          <div class="dev-row__title">MCP link</div>
          <div class="dev-row__sub">Copy and paste this link to connect VTM to Claude, ChatGPT and other AI assistants</div>
          <copy-field :value="mcpUrl" label="MCP link" class="mcp__link"></copy-field>
        </div>

        <!-- 如何连接 -->
        <div class="dev-acc" :class="{ 'is-open': open.connect }">
          <button type="button" class="dev-acc__head" :aria-expanded="open.connect" @click="toggle('connect')">
            <span class="dev-row__text">
              <span class="dev-row__title">How to connect</span>
              <span class="dev-row__sub">Step-by-step for Claude, ChatGPT, Claude Code, Cursor and VS Code. The link works with any MCP client.</span>
            </span>
            <i class="el-icon-arrow-down dev-acc__chev"></i>
          </button>
          <div v-if="open.connect" class="dev-acc__body">
            <div class="mcp__clients" role="tablist">
              <button
                v-for="client in clients"
                :key="client.id"
                type="button"
                role="tab"
                class="mcp__client"
                :class="{ 'is-active': client.id === clientId }"
                :aria-selected="client.id === clientId"
                @click="clientId = client.id"
              >
                {{ client.name }}
              </button>
            </div>
            <div class="mcp__client-hint">{{ activeClient.hint }}</div>
            <ol class="mcp__steps">
              <li v-for="(step, i) in activeClient.steps" :key="i">
                <span v-html="step"></span>
                <copy-field
                  v-if="i === 0 && activeClient.command"
                  :value="activeClient.command"
                  :block="activeClient.command.indexOf('\n') !== -1"
                  label="command"
                  class="mcp__cmd"
                ></copy-field>
                <a v-if="i === 0 && activeClient.installUrl" class="mcp__install" :href="activeClient.installUrl">
                  <i class="el-icon-plus"></i>{{ activeClient.installLabel }}
                </a>
              </li>
            </ol>
          </div>
        </div>

        <!-- 断开 -->
        <div class="dev-acc" :class="{ 'is-open': open.disconnect }">
          <button type="button" class="dev-acc__head" :aria-expanded="open.disconnect" @click="toggle('disconnect')">
            <span class="dev-row__text">
              <span class="dev-row__title">How to disconnect</span>
              <span class="dev-row__sub">Remove access from an assistant at any time</span>
            </span>
            <i class="el-icon-arrow-down dev-acc__chev"></i>
          </button>
          <div v-if="open.disconnect" class="dev-acc__body">
            <ol class="mcp__steps">
              <li>Remove <b>VTM Vending</b> from the assistant's connector settings.</li>
              <li>
                To cut access off from our side as well, open
                <a href="#" @click.prevent="$emit('go', 'keys')">API keys</a>
                and revoke the keys marked <b>AI assistant</b>. Each sign-in creates one.
              </li>
            </ol>
          </div>
        </div>
      </div>
    </section>

    <!-- MCP 工具 -->
    <section class="dev-section">
      <div class="dev-card">
        <div class="dev-acc" :class="{ 'is-open': open.tools }">
          <button type="button" class="dev-acc__head" :aria-expanded="open.tools" @click="toggle('tools')">
            <span class="dev-row__text">
              <span class="dev-row__title">MCP tools</span>
              <span class="dev-row__sub">{{ tools.length }} tools active · all read-only</span>
            </span>
            <i class="el-icon-arrow-down dev-acc__chev"></i>
          </button>
          <div v-if="open.tools" class="dev-acc__body">
            <ul class="mcp__tools">
              <li v-for="tool in tools" :key="tool.name" class="mcp__tool">
                <div class="mcp__tool-head">
                  <code class="mcp__tool-name">{{ tool.name }}</code>
                  <span class="mcp__tool-title">{{ tool.title }}</span>
                </div>
                <div class="mcp__tool-desc">{{ tool.description }}</div>
                <div class="mcp__tool-ask"><i class="el-icon-chat-line-round"></i>“{{ tool.ask }}”</div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
  // AI 助手（MCP）连接面板：布局参照 Lovable「Agent integrations」——概览 / MCP 链接 / 连接步骤 / 工具清单
  import CopyField from "./CopyField";
  import { MCP_TOOLS, mcpClients } from "./developerContent";

  export default {
    name: "McpPanel",
    components: { CopyField },
    props: {
      mcpUrl: { type: String, required: true }
    },
    data() {
      return {
        clientId: "claude",
        open: { connect: true, disconnect: false, tools: false },
        tools: MCP_TOOLS
      };
    },
    computed: {
      clients() {
        return mcpClients(this.mcpUrl);
      },
      activeClient() {
        return this.clients.find(c => c.id === this.clientId) || this.clients[0];
      }
    },
    methods: {
      toggle(name) {
        this.open[name] = !this.open[name];
      }
    }
  };
</script>

<style scoped lang="scss">
  .mcp__link {
    margin-top: 12px;
  }

  .mcp__clients {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .mcp__client {
    border: 1px solid var(--vtm-line-2);
    background: var(--vtm-surface);
    color: var(--vtm-ink-2);
    border-radius: 20px;
    padding: 5px 14px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all var(--vtm-speed);

    &:hover {
      color: var(--vtm-ink);
      border-color: var(--vtm-ink-3);
    }

    &.is-active {
      background: var(--vtm-navy);
      border-color: var(--vtm-navy);
      color: #fff;
    }

    &:focus-visible {
      outline: none;
      box-shadow: var(--vtm-focus-ring);
    }
  }

  :root[data-theme="dark"] .mcp__client.is-active {
    background: var(--vtm-mint);
    border-color: var(--vtm-mint);
    color: #00001b;
  }

  .mcp__client-hint {
    margin: 10px 0 4px;
    font-size: 12.5px;
    color: var(--vtm-ink-3);
  }

  .mcp__steps {
    margin: 8px 0 0;
    padding-left: 20px;
    color: var(--vtm-ink);
    font-size: 13.5px;
    line-height: 1.6;

    li {
      margin-bottom: 8px;
      padding-left: 4px;
    }

    a {
      color: var(--vtm-mint-ink);
      font-weight: 600;
    }
  }

  .mcp__cmd {
    margin-top: 8px;
  }

  .mcp__install {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 10px;
    padding: 7px 14px;
    border-radius: var(--vtm-r-sm);
    background: var(--vtm-navy);
    color: #fff !important;
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;

    &:hover {
      background: var(--vtm-navy-3);
    }
  }

  .mcp__tools {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 10px;
  }

  .mcp__tool {
    border: 1px solid var(--vtm-line);
    border-radius: var(--vtm-r-md);
    padding: 12px 14px;
    background: var(--vtm-surface-2);
  }

  .mcp__tool-head {
    display: flex;
    align-items: baseline;
    gap: 8px;
    flex-wrap: wrap;
  }

  .mcp__tool-name {
    font-family: var(--vtm-mono);
    font-size: 12.5px;
    color: var(--vtm-mint-ink);
    font-weight: 600;
  }

  .mcp__tool-title {
    font-size: 12.5px;
    color: var(--vtm-ink-3);
  }

  .mcp__tool-desc {
    margin-top: 6px;
    font-size: 13px;
    color: var(--vtm-ink);
    line-height: 1.5;
  }

  .mcp__tool-ask {
    margin-top: 8px;
    font-size: 12.5px;
    color: var(--vtm-ink-2);
    font-style: italic;

    i {
      margin-right: 5px;
      font-style: normal;
    }
  }
</style>
